import React, { useState } from 'react';
import { Check, Copy, Download, Link2, QrCode, Share2, Sparkles } from 'lucide-react';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import { getThemeColor } from '../../config/appConfig';
import { copyTextToClipboard } from './qrHelpers';
import QrCopyField from './QrCopyField';

const THEME = getThemeColor('primary');

const QrStudio = ({
  title,
  subtitle,
  codeLabel = 'Partner Code',
  urlLabel = 'URL',
  codeValue,
  urlValue,
  qrSource,
  onSourceChange,
  onGenerate,
  qrGenerating,
  qrError,
  qrDataUrl,
  qrLabel,
  downloadName,
  error,
}) => {
  const [linkCopied, setLinkCopied] = useState(false);

  const activeValue = qrSource === 'code' ? codeValue : urlValue;
  const sourceLabel = qrSource === 'code' ? codeLabel : urlLabel;

  const handleCopyLink = async () => {
    if (!qrLabel) return;
    try {
      const ok = await copyTextToClipboard(qrLabel);
      if (!ok) return;
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      setLinkCopied(false);
    }
  };

  const handleShare = async () => {
    if (!qrLabel) return;
    try {
      if (navigator.share) {
        const isUrl = /^https?:\/\//i.test(qrLabel);
        await navigator.share({
          title,
          text: isUrl ? `Scan or open this ${sourceLabel}` : `${sourceLabel}: ${qrLabel}`,
          url: isUrl ? qrLabel : undefined,
        });
        return;
      }
      await handleCopyLink();
    } catch {
      /* user cancelled share */
    }
  };

  return (
    <div className="page qr-page" style={{ '--primary': THEME, '--qr-red': THEME }}>
      <div className="page-inner">
        <ModulePageHeader
          icon={QrCode}
          title={title}
          subtitle={subtitle}
          iconBg={`linear-gradient(135deg,${THEME},#111111)`}
        />
        <ModuleAlert message={error} />

        <div className="qr-layout">
          <section className="qr-panel">
            <span className="qr-panel__eyebrow">
              <Sparkles className="w-3 h-3" />
              QR Studio
            </span>
            <h2 className="qr-panel__title">Choose source & generate</h2>
            <p className="qr-panel__desc">
              Switch between code and URL, then create a scannable QR with one click.
            </p>

            <div className="qr-segment" role="radiogroup" aria-label="QR source">
              <button
                type="button"
                role="radio"
                aria-checked={qrSource === 'code'}
                className={`qr-segment__btn${qrSource === 'code' ? ' is-active' : ''}`}
                onClick={() => onSourceChange('code')}
              >
                <Link2 className="w-4 h-4" />
                By Code
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={qrSource === 'url'}
                className={`qr-segment__btn${qrSource === 'url' ? ' is-active' : ''}`}
                onClick={() => onSourceChange('url')}
              >
                <QrCode className="w-4 h-4" />
                By URL
              </button>
            </div>

            <div className="qr-fields">
              <QrCopyField label={codeLabel} value={codeValue} active={qrSource === 'code'} />
              <QrCopyField label={urlLabel} value={urlValue} active={qrSource === 'url'} />
            </div>

            <button
              type="button"
              className="qr-generate"
              onClick={onGenerate}
              disabled={qrGenerating || !activeValue}
            >
              <QrCode className="w-5 h-5" />
              {qrGenerating ? 'Generating…' : 'Generate QR Code'}
            </button>

            {qrError ? <p className="qr-error">{qrError}</p> : null}
          </section>

          <section className="qr-panel qr-preview">
            {qrDataUrl ? (
              <>
                <span className="qr-ready">
                  <span className="qr-ready__dot" aria-hidden="true" />
                  Ready to Scan
                </span>

                <div className="qr-frame">
                  <div className="qr-frame__corners" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                    <span />
                  </div>
                  <div className="qr-frame__inner">
                    <img src={qrDataUrl} alt={`${title} preview`} className="qr-frame__img" />
                  </div>
                </div>

                <p className="qr-preview__source">Generated from {sourceLabel}</p>
                <p className="qr-preview__value">{qrLabel}</p>

                <div className="qr-actions-bar">
                  <a
                    href={qrDataUrl}
                    download={downloadName}
                    className="qr-action qr-action--primary"
                  >
                    <Download className="w-4 h-4" />
                    Download QR
                  </a>
                  <button type="button" className="qr-action" onClick={handleCopyLink}>
                    {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {linkCopied ? 'Copied' : 'Copy Link'}
                  </button>
                  <button type="button" className="qr-action" onClick={handleShare}>
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                </div>
              </>
            ) : (
              <div className="qr-preview__empty">
                <div className="qr-preview__empty-icon">
                  <QrCode className="w-7 h-7" />
                </div>
                <p className="qr-preview__empty-title">Your QR will appear here</p>
                <p className="qr-preview__empty-text">
                  Select <strong>By Code</strong> or <strong>By URL</strong>, then tap Generate QR Code
                  for a ready-to-scan preview.
                </p>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default QrStudio;
